import { useMemo, useState } from "react";
import { notification } from "antd";

import { getNSPDRawJson } from "@/api/nspd";
import { getObjectsOptions } from "@/api/objects";
import { convertNspdValue } from "@/utils/NspdSync/convertNspdValue";
import { getNspdRawValue } from "@/utils/NspdSync/getValueByPath";
import { isEqualValue } from "@/utils/NspdSync/isEqualValue";

const initialNspdSyncState = {
  appliedFields: [],
  failedFields: [],
  syncedFieldNames: [],
  valuesAfterSync: {},
  isSuccessful: false,
  isDirtyByNspdField: false,
};

export const useNspdSync = ({
  data,
  allFields,
  setData,
  setEditable,
  setIsChanged,
  message,
  isAdmins = false,
}) => {
  const [nspdSync, setNspdSync] = useState(initialNspdSyncState);
  const [isNspdLoading, setIsNspdLoading] = useState(false);

  const nspdFields = useMemo(() => {
    return allFields.filter((field) => field.nspd_key && field.nspd_key_uuid);
  }, [allFields]);

  const resetNspdSync = () => {
    setNspdSync(initialNspdSyncState);
  };

  const markNspdFieldAsDirty = (fieldName) => {
    const isNspdField = nspdSync.syncedFieldNames.includes(fieldName);

    if (!isNspdField || !nspdSync.isSuccessful) return;

    setNspdSync((prev) => ({
      ...prev,
      isDirtyByNspdField: true,
      isSuccessful: false,
    }));
  };

  const handleNspdSync = async (cadastre_number) => {
    if (!nspdFields.length) {
      message.warning("Для объекта не настроены поля для синхронизации с НСПД");
      return;
    }

    if (!cadastre_number) {
      message.warning("Не заполнен кадастровый номер");
      return;
    }

    setIsNspdLoading(true);

    try {
      const json = await getNSPDRawJson(cadastre_number);

      const appliedFields = [];
      const failedFields = [];
      const syncedFieldNames = [];

      const nextData = { ...data };

      const repositoryOptionsCache = new Map();

      for (const field of nspdFields) {
        syncedFieldNames.push(field.field_name);

        const rawValue = getNspdRawValue(json, field);
        const converted =
          field.type === "repository"
            ? await convertRepositoryNspdValue({
                rawValue,
                field,
                currentValue: data[field.field_name],
                unitUuid: data.unit_uuid,
                isAdmins,
                cache: repositoryOptionsCache,
              })
            : convertNspdValue(rawValue, field);

        if (!converted.ok) {
          failedFields.push({
            field_name: field.field_name,
            name: field.caption || field.name,
            reason: converted.reason,
          });

          continue;
        }

        const oldValue = data[field.field_name];
        const isChanged =
          field.type === "repository"
            ? !isEqualValue(getUuidValue(oldValue), converted.value)
            : !isEqualValue(oldValue, converted.value);

        appliedFields.push({
          field_name: field.field_name,
          name: field.caption || field.name,
          oldValue,
          newValue: converted.value,
          isChanged,
        });

        if (isChanged) {
          nextData[field.field_name] = converted.value;
        }
      }

      setData(nextData);
      setEditable(true);

      if (appliedFields.length) {
        setIsChanged(true);
      }

      setNspdSync({
        appliedFields,
        failedFields,
        syncedFieldNames,
        valuesAfterSync: nspdFields.reduce((acc, field) => {
          acc[field.field_name] = nextData[field.field_name];
          return acc;
        }, {}),
        isSuccessful: nspdFields.length > 0 && failedFields.length === 0,
        isDirtyByNspdField: false,
      });

      if (failedFields.length) {
        notification.warning({
          message: "Синхронизация выполнена частично",
          description: (
            <div>
              Не обновлены поля:{" "}
              {failedFields.map((item) => item.name).join(", ")}
            </div>
          ),
          duration: 10,
        });

        return;
      }

      if (appliedFields.length) {
        message.success("Данные НСПД синхронизированы");
      } else {
        message.info("Данные НСПД совпадают с текущими значениями");
      }
    } catch (error) {
      message.error(error.message || "Не удалось получить данные НСПД");
    } finally {
      setIsNspdLoading(false);
    }
  };

  return {
    nspdSync,
    isNspdLoading,
    handleNspdSync,
    resetNspdSync,
    markNspdFieldAsDirty,
  };
};

const getUuidValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value.uuid ?? null;
  }

  return value ?? null;
};

const normalizeRepositoryName = (value) => {
  if (typeof value === "string") return value.trim();

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }

  return "";
};

const getRepositoryOptions = async ({ field, unitUuid, isAdmins, cache }) => {
  if (!field.object) {
    throw new Error("Не выбран справочник для поля");
  }

  const cacheKey = `${field.object}:${unitUuid || ""}`;

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const { objects = [] } = await getObjectsOptions(
    {
      definition_uuid: field.object,
      ...(unitUuid ? { unit_uuid: unitUuid } : {}),
    },
    isAdmins,
  );

  cache.set(cacheKey, objects);

  return objects;
};

const convertRepositoryNspdValue = async ({
  rawValue,
  field,
  currentValue,
  unitUuid,
  isAdmins,
  cache,
}) => {
  const name = normalizeRepositoryName(rawValue);

  if (!name) {
    return {
      ok: false,
      reason: "Связанное поле не найдено в ответе НСПД",
    };
  }

  try {
    const options = await getRepositoryOptions({
      field,
      unitUuid,
      isAdmins,
      cache,
    });
    const currentUuid = getUuidValue(currentValue);
    const currentOption = currentUuid
      ? options.find((option) => option.uuid === currentUuid)
      : null;

    if ((currentValue?.name || currentOption?.name) === name && currentUuid) {
      return { ok: true, value: currentUuid };
    }

    const matchedOption = options.find((option) => option.name === name);

    if (!matchedOption) {
      return {
        ok: false,
        reason: "Значение не найдено в справочнике",
      };
    }

    return { ok: true, value: matchedOption.uuid };
  } catch (error) {
    return {
      ok: false,
      reason: error.message || "Не удалось получить значения справочника",
    };
  }
};
